import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';

/** One cache entry per basin filter; the list is small and rarely changes. */
export const COLLECTIONS_QUERY_KEY = 'cross-section:collections' as const;
export const COLLECTION_QUERY_KEY = 'cross-section:collection' as const;

/** How long a collection list or its grids stay fresh, in ms. */
const STALE_MS = 300_000;

/** A collection as the list query returns it — no grids, just the header. */
export interface CollectionSummary {
  id: number;
  collectionName: string;
  basin: string;
  owner: string | null;
  isDefault: boolean;
  validity: string;
  /** How many geologic intervals the collection covers. */
  intervals: number;
  /** When the collection was made, ISO-8601. Breaks ties between candidates. */
  createdAt: string;
}

interface CollectionsResponse {
  totalCount: number;
  results: CollectionSummary[];
}

/** A grid in a collection, with the provenance the settings panel shows. */
export interface CollectionGrid {
  /** The grid record itself, which the picker links its Grid Surface name to. */
  href: string;
  structureGridId: number;
  gridSurface: string;
  geologicInterval: string;
  geologicIntervalPositionalIndex: number | null;
  geologicIntervalDisplayColor: string | null;
  source: string | null;
  owner: string | null;
  validity: string;
  avgThickness: number | null;
  /**
   * The interval's basin, as the Structure Grid Management table names it. Absent from
   * the collection endpoint, which reads the join row rather than the grid — so a grid
   * arriving that way has none, and the picker's column says as much.
   */
  basinName: string | null;
  /** The grid's cell dimensions, in feet. Null where the grid never recorded them. */
  cellWidthFeet: number | null;
  cellHeightFeet: number | null;
  /** When the grid record was made, ISO-8601. */
  createdAt: string | null;
}

interface CollectionResponse {
  id: number;
  collectionName: string;
  basin: string;
  isDefault: boolean;
  validity: string;
  structureGridCollectionItems: CollectionGrid[];
}

/**
 * Every Structure Grid Collection available to cut a section from.
 *
 * Unpaginated on purpose: the settings panel's dropdown lists them all, and a pager
 * inside a dropdown would hide the one collection someone is looking for. The set is
 * small — one per basin per owner — so this is a list read, not a table scan.
 *
 * @param {boolean} enabled - Whether to fetch at all; false while the tool is idle.
 * @return {object} The collections, load state, and any error.
 */
export const useStructureGridCollections = (enabled: boolean) => {
  const { data, isFetching, error } = useQuery<CollectionsResponse, Error>({
    queryKey: [COLLECTIONS_QUERY_KEY],
    // `wrap_parameters :query` on the controller, so the body nests under `query`.
    queryFn: ({ signal }) =>
      api
        .post('queries/structure_grid_collections', {
          json: { query: { paginated: false } },
          signal,
        })
        .json<CollectionsResponse>(),
    enabled,
    staleTime: STALE_MS,
  });

  return {
    collections: data?.results ?? [],
    loading: isFetching,
    error: error ?? null,
  };
};

/**
 * One collection's grids, in the order the intervals stack.
 *
 * The list query returns collection headers only, so the grids — and the interval,
 * source, owner and validity the panel shows for each — come from the collection
 * itself.
 *
 * @param {number | null} collectionId - The collection to read, or null for none.
 * @return {object} The grids, load state, and any error.
 */
export const useStructureGridCollection = (collectionId: number | null) => {
  const { data, isFetching, error } = useQuery<CollectionResponse, Error>({
    queryKey: [COLLECTION_QUERY_KEY, collectionId],
    queryFn: ({ signal }) =>
      api
        .get(`structure_grid_collections/${collectionId}`, { signal })
        .json<CollectionResponse>(),
    enabled: collectionId !== null,
    staleTime: STALE_MS,
  });

  return {
    collection: data ?? null,
    grids: data?.structureGridCollectionItems ?? [],
    loading: isFetching,
    error: error ?? null,
  };
};
